'use client';

import useEmblaCarousel from 'embla-carousel-react';
import clsx from 'clsx';
import { Dots } from './EmblaDotButtons';
import { CarouselProps } from './types';
import { NextButton, PrevButton } from './EmblaArrowButtons';
import usePrevNextButtons from './hooks/usePrevNextButtons';
import useDotButton from './hooks/useDotButton';

import styles from './EmblaCarousel.module.css';

const EmblaCarousel = <T,>({
  slides,
  options,
  showArrows = true,
  showDots = true,
  className,
  slideContentClassName,
  containerClassName,
  backArrowClasses,
  nextArrowClasses,
  dotClassName,
}: CarouselProps<T>) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    containScroll: 'trimSnaps',
    ...options,
  });

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);

  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

  return (
    <div className={clsx(styles.embla, styles.carouselContent, className)}>
      <div className={'emblaViewport'} ref={emblaRef}>
        <div className={clsx(styles.embla__container, 'emblaContainer', containerClassName)}>
          {slides.map((slide, index) => (
            <div className={clsx(styles.embla__slide, 'emblaSlide', slideContentClassName)} key={index}>
              {slide}
            </div>
          ))}
        </div>
      </div>

      {showArrows ? (
        <>
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} backArrowClasses={backArrowClasses} />
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} nextArrowClasses={nextArrowClasses} />
        </>
      ) : null}

      {showDots && scrollSnaps.length > 1 ? (
        <Dots
          emblaApi={emblaApi}
          scrollSnaps={scrollSnaps}
          selectedIndex={selectedIndex}
          onclick={onDotButtonClick}
          dotClassName={dotClassName}
        />
      ) : null}
    </div>
  );
};

export default EmblaCarousel;
